import { Injectable } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { LocalStorageService } from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class AgrimotorService {

  constructor(private sharedService: SharedService, private sessionSt: LocalStorageService) { }

  // Get Access Token
  getAccessToken(data) {
    return this.sharedService.postAgrimotorAPI('oauth/token', data);
  }

  // Get All Static Data (directly from Agri Motor external API - like Amlin calls horsebox-api/lists)
  getStaticData(authDetails) {
    return this.sharedService.getAgrimotorAPI('agri-api/lists', authDetails);
  }

  // Get Agriculture Quote Data
  getAgrimotorData(id) {
    return this.sharedService.getAPI('agriculture/quote/' + id);
  }

  // Save Quote
  saveQuote(data) {
    return this.sharedService.postAPI('agriculture/quote', data);
  }

  // Calculate Premium
  calculatePremium(data, authDetails) {
    return this.sharedService.postAgrimotorAPI('agri-api/quote/calculate', data, authDetails);
  }

  // Get Quote Details
  getQuoteDetails(authDetails, id) {
    return this.sharedService.getAgrimotorAPI('agri-api/quote/' + id, authDetails);
  }

  // Get Unique ID for Drivers
  getUniqueID(authDetails) {
    return this.sharedService.getAgrimotorAPI('agri-api/generate-id', authDetails);
  }

  // Issue The Quote
  issueQuote(data) {
    return this.sharedService.postAPI('agriculture/issuequote', data);
  }

  // Get Cancellation Return Premium
  getReturnPremium(data, policy_id, authDetails) {
    return this.sharedService.postAgrimotorAPI('agri-api/policy/' + policy_id + ':return-premium', data, authDetails);
  }

  // Cancel Policy
  getCancelPolicy(data, policy_id, authDetails) {
    return this.sharedService.postAgrimotorAPI('agri-api/policy/' + policy_id + ':cancel', data, authDetails);
  }

  // Get Vehicle Details (DVLA lookup)
  getVehicleDetails(registration_number, authDetails) {
    return this.sharedService.getAgrimotorAPI('agri-api/vehicle/' + registration_number, authDetails);
  }

  // Get Agreed Questions for Pre-qualifier
  getAgreedQuestions(productId, version) {
    return this.sharedService.getAPI('staticdata/agreed_questions/' + productId + '/' + version);
  }

  // Get Quote Details by Quote ID (for pre-qualifier)
  getQuoteDetailsByQuoteID(id) {
    return this.sharedService.getAPI('agriculture/quote-details/' + id);
  }

  // Regenerate Policy/Quote Documents
  regenerateDocuments(quoteId, isPolicy) {
    return this.sharedService.getAPI('agriculture/regenerate/policy/documents/' + quoteId + '/' + isPolicy);
  }

  // ─── Response shape helpers ────────────────────────────────────────────────
  // `agriculture_data` comes back wrapped in a JSON-API `data` envelope from some
  // endpoints and unwrapped from others, and the application payload sits either
  // under `attributes.application` or directly under `attributes`. Reading one exact
  // path meant a save response in the "other" shape silently produced nothing, which
  // is why proposer/fleet details stopped sticking when navigating back through the
  // tabs. Everything goes through these two helpers instead.

  /** The `{ id, attributes }` envelope of a quote version, whichever shape it arrived in. */
  agricultureEnvelope(version: any): any {
    const ag = version?.agriculture_data;
    if (!ag) { return null; }
    return ag.data ? ag.data : ag;
  }

  /** The application attributes of a quote version (the object the forms bind to). */
  applicationOf(version: any): any {
    const envelope = this.agricultureEnvelope(version);
    const attributes = envelope?.attributes;
    if (!attributes) { return null; }
    return attributes.application ? attributes.application : attributes;
  }

  /** Fields that live on the envelope's attributes rather than inside `application`. */
  quoteMetaOf(version: any): { id: any, reference: any, status: any, premium: any } {
    const envelope = this.agricultureEnvelope(version);
    const attributes = envelope?.attributes || {};
    return {
      id: envelope?.id,
      reference: attributes.reference,
      status: attributes.status,
      premium: attributes.premium,
    };
  }
}
